import {useState} from 'react';
import {
  API,
  VERIFICATION_METHODS,
  VERIFICATION_PURPOSES,
  VerificationMethod,
} from '@chekin/api';
import {PATTERNS} from '@chekin/core';

type Props = {
  type: VerificationMethod;
  reservationId?: string;
  onGenerate?: () => void;
  onVerify?: (data: string, isVerified: boolean) => void | Promise<void>;
  isMock?: boolean;
};

const MOCK_VERIFICATION_CODE = '123456';
const MOCK_DELAY_MS = 1000;

const mockPromise = () =>
  new Promise<true>(resolve => setTimeout(() => resolve(true), MOCK_DELAY_MS));

const validateTarget = (data: string, method: VerificationMethod) => {
  if (method === VERIFICATION_METHODS.email) {
    const emailRegex = PATTERNS.email;
    return emailRegex.test(data);
  }
  if (method === VERIFICATION_METHODS.phone) {
    const phoneRegex = PATTERNS.phone;
    return phoneRegex.test(data);
  }
  return false;
};

const useVerification = ({
  type,
  reservationId,
  onGenerate = () => {},
  onVerify = () => {},
  isMock = import.meta.env.DEV,
}: Props) => {
  const [isLoading, setIsLoading] = useState(false);
  const [contactsData, setContactsData] = useState<string>('');

  const onSubmit = async (data: string) => {
    setIsLoading(true);
    try {
      if (!data || !reservationId) return;
      if (!validateTarget(data, type)) {
        throw new Error(
          type === VERIFICATION_METHODS.email ? 'invalid_email' : 'invalid_phone_number',
        );
      }
      await (isMock
        ? mockPromise()
        : API.verification.generate({
            method: type,
            target: data.trim(),
            reservation: reservationId,
            purpose: VERIFICATION_PURPOSES.contactVerification,
          }));

      setContactsData(data);
      onGenerate();
    } finally {
      setIsLoading(false);
    }
  };

  const verifyCode = async (code: string) => {
    setIsLoading(true);
    try {
      if (!reservationId) return;
      const result = await (isMock
        ? mockPromise().then(() => code === MOCK_VERIFICATION_CODE)
        : API.verification
            .verify({
              method: type,
              target: String(contactsData.trim()),
              credential: code,
              reservation: reservationId,
              purpose: VERIFICATION_PURPOSES.contactVerification,
            })
            .then(r => r.verified));

      if (!result) {
        throw new Error('verification_failed');
      }
      await onVerify?.(contactsData, !!(contactsData && code));
    } finally {
      setIsLoading(false);
    }
  };

  const resendCode = async () => {
    setIsLoading(true);
    try {
      if (!reservationId) return;
      await (isMock
        ? mockPromise()
        : API.verification.generate({
            method: type,
            target: String(contactsData.trim()),
            reservation: reservationId,
            purpose: VERIFICATION_PURPOSES.contactVerification,
          }));
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    contactsData,
    onSubmit,
    verifyCode,
    resendCode,
  };
};

export {useVerification};
